/**
 * Default: layout > Menu
 */

import { debounce } from '@ifabnpm/bootstrap/utils.js';

import SelectorEngine from '@ifabnpm/bootstrap/dom/selector-engine.js';
import Manipulator from '@ifabnpm/bootstrap/dom/manipulator.js';
import EventHandler from '@ifabnpm/bootstrap/dom/event-handler.js';

import Nav from '@ifabnpm/bootstrap/component/nav.js';
import Drawer from '@ifabnpm/bootstrap/component/drawer.js';

// -------
// Private
// -------

const NAME = 'menu';
const DATA_KEY = `ifab.${NAME}`;
const EVENT_KEY = `.${DATA_KEY}`;
// const API_KEY    = '.data-api';

const pageMenu = SelectorEngine.findOne('#page-menu-drawer');

let mainNavInstance;

/**
 * Höhe von `.drawer__body` an die Höhe der aktuell sichtbaren Liste (Haupt-
 * oder Untermenü) anpassen, damit sich das komplette Drawer-Panel (nicht nur
 * `.main-nav`) sichtbar auf die passende Höhe animiert (siehe
 * `--ifabsh-page-menu-body-height` in `_index.scss`).
 *
 * @param {HTMLElement|Node|null} list
 */
const updateNavHeight = (list) => {
	const drawerBody = SelectorEngine.findOne('.drawer__body', pageMenu);

	if (!drawerBody || !list)
	{
		return;
	}

	// `.drawer__body` rechnet (per `box-sizing: border-box`) sein eigenes
	// Padding in die Höhe mit ein, daher hier draufrechnen, damit für die
	// Liste selbst genug Platz bleibt.
	const styles = window.getComputedStyle(drawerBody);
	const paddingBlock = Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom);

	drawerBody.style.setProperty('--ifabsh-page-menu-body-height-absolute', `${list.offsetHeight + paddingBlock}px`);
	drawerBody.style.setProperty('--ifabsh-page-menu-body-height', `${list.offsetHeight}px`);
};

/**
 * Aktuell sichtbare Liste (Haupt- oder Untermenü) der Navigation bestimmen.
 *
 * @returns {HTMLElement|Node|null}
 */
const getCurrentMenuList = () => {
	if (!mainNavInstance)
	{
		return null;
	}

	const openedButton = mainNavInstance.getCurrentMenu();
	const controls      = openedButton ? Manipulator.getAria(openedButton, 'controls') : null;

	return (controls && SelectorEngine.findOne(`#${controls}`)) || mainNavInstance.menu;
};

/**
 * Drawer (Navigationsoverlay)
 */
const setupDrawer = () => {
	const trigger = SelectorEngine.findOne('[aria-controls="page-menu-drawer"]');
	const closeBtn = SelectorEngine.findOne('[data-drawer-close]', pageMenu);
	const drawer = new Drawer(pageMenu, {
		closeButton: false,
		onShow: function() {
			Manipulator.addClass(trigger, '-active');
		},
		onShown: () => {
			// Während der Drawer `hidden` war, konnte die tatsächliche Höhe nicht
			// gemessen werden – jetzt, wo er sichtbar ist, nachholen.
			updateNavHeight(getCurrentMenuList());
		},
		onHidden: () => {
			Manipulator.removeClass(trigger, '-active');
		}
	});

	EventHandler.on(trigger, 'click.ifab.pageMenuDrawer', (event) => {
		event.preventDefault();
		event.stopPropagation();

		if (event.delegateTarget.classList.contains('-active'))
		{
			drawer.hide();
		}
		else
		{
			drawer.show();
		}
	});

	if (closeBtn)
	{
		EventHandler.on(closeBtn, 'click.ifab.pageMenuDrawer', () => {
			drawer.hide();
		});
	}
};

/**
 * Initialisierung der eigentlichen Navigationsliste.
 */
const navigation = () => {
	const nav = SelectorEngine.findOne('#main-nav');

	if (nav)
	{
		mainNavInstance = new Nav(nav, {
			closeByOutsideClick: false,
			closeByEsc: false,
			labelSubmenu: (window.pageInstance.tm) ? window.pageInstance.tm.translate('ariaLabelSubmenu') : 'Submenu for %s',
			labelSubmenuTrigger: (window.pageInstance.tm) ? window.pageInstance.tm.translate('ariaLabelSubmenuTrigger') : 'Show submenu for %s',
			// Die Slide-/Fade-Animation der Untermenüs läuft bereits vollständig über
			// die CSS-Transition auf `[aria-hidden]` (siehe `elements/_nav.scss`).
			// `Reveal` würde zusätzlich per Inline-Styles eine eigene Höhenanimation
			// einschieben, die mit dieser CSS-Transition kollidiert (sie funktioniert
			// dadurch verlässlich nur beim allerersten Ein-/Ausblenden). Die Höhe des
			// Panels wird stattdessen separat über `updateNavHeight()` animiert.
			useReveal: false,
			onHide: (event) => {
				const parentList = event.relatedMenu && event.relatedMenu.parentElement
					? event.relatedMenu.parentElement.closest('.nav-list')
					: null;

				updateNavHeight(parentList || mainNavInstance.menu);
			},
			onHidden: () => {
				// Erst wenn wirklich kein Untermenü mehr geöffnet ist, die oberste Ebene
				// wieder einblenden.
				if (!mainNavInstance.getCurrentMenu())
				{
					Manipulator.removeClass(nav, '-is-suspended');
				}
			},
			onShow: (event) => {
				Manipulator.addClass(nav, '-is-suspended');
				updateNavHeight(event.relatedMenu);
			}
		});

		mainNavInstance.showCurrentTree();

		EventHandler.on(nav, 'click', '.nav-link.-back', (event) => {
			event.preventDefault();
			mainNavInstance.close();
		});

		window.addEventListener('resize', debounce(() => {
			updateNavHeight(getCurrentMenuList());
		}));
	}
};

// -------
// Public
// -------

const menu = () => {
	if (pageMenu)
	{
		setupDrawer();
		navigation();
	}
};

// Export
export default menu;
